import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShoppingBag, Wallet, TrendingUp, Truck, RotateCcw, Activity,
  ShoppingCart, CreditCard, RefreshCw,
  Boxes, AlertTriangle, Users, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useRealtime } from "@/lib/realtime-context";
import { getDashboardOverview, refreshDashboard } from "@/lib/dashboard";

import KpiTile from "@/pages/dashboard/KpiTile";
import LiveOpCard from "@/pages/dashboard/LiveOpCard";
import SectionHeader from "@/pages/dashboard/SectionHeader";
import AlertsPanel from "@/pages/dashboard/AlertsPanel";
import ActivityFeed from "@/pages/dashboard/ActivityFeed";
import DeliveryCard from "@/pages/dashboard/DeliveryCard";
import ReturnsCard from "@/pages/dashboard/ReturnsCard";
import CashflowCard from "@/pages/dashboard/CashflowCard";
import { Can } from "@/lib/can";
import { useAuth } from "@/lib/auth";

const POLL_MS = 30_000;

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { status, subscribe } = useRealtime();
  const { hasPermission } = useAuth();
  const canSeeFinance = hasPermission("overview.full");
  const canSeeDelivery = hasPermission("delivery.view");
  const canSeeReturns = hasPermission("returns.view");
  const loadRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const d = await getDashboardOverview();
      setData(d);
    } catch (e) {
      console.warn("overview load failed", e);
    } finally {
      setLoading(false);
    }
  }, []);
  loadRef.current = load;

  // initial + polling fallback
  useEffect(() => {
    load();
    const t = setInterval(() => loadRef.current?.(), POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  // Realtime triggers — debounced refresh on any business event.
  useEffect(() => {
    let timer = null;
    const debounced = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => loadRef.current?.(), 800);
    };
    const topics = [
      "order.created", "order.updated", "order.cancelled",
      "fulfillment.created", "fulfillment.updated", "refund.created",
      "return.created", "return.updated",
      "service.created", "service.updated",
    ];
    const offs = topics.map((t) => subscribe(t, debounced));
    return () => { if (timer) clearTimeout(timer); offs.forEach((o) => o && o()); };
  }, [subscribe]);

  const handleManualRefresh = async () => {
    setRefreshing(true);
    try {
      const fresh = await refreshDashboard();
      setData(fresh);
      toast.success("Dashboard actualizat");
    } catch {
      toast.error("Refresh eșuat");
    } finally {
      setRefreshing(false);
    }
  };

  const k = data?.kpis || {};
  const sp = k.spark || {};
  const liveOps = data?.live_ops || {};
  const alerts = data?.alerts || [];

  return (
    <div className="space-y-10 pb-12" data-testid="dashboard-page">
      {/* === HEADER === */}
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-2">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary" /> Business Command Center
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
            Hub operațional · Live
          </h1>
          <p className="text-muted-foreground text-sm mt-1.5">
            Imagine completă peste comenzi, livrări, retururi, suport și cashflow — în timp real.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <RealtimePill status={status} />
          <Button
            variant="outline"
            size="sm"
            onClick={handleManualRefresh}
            disabled={refreshing}
            data-testid="dashboard-refresh-btn"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </header>

      {/* === SECTION 1: GLOBAL KPIs === */}
      <SectionHeader
        title="KPI globale · Azi"
        subtitle="Comparație vs ziua precedentă"
        testid="section-kpis"
      >
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {loading ? Array.from({ length: 10 }).map((_, i) => <KpiSkeleton key={i} />) : (
            <>
              <KpiTile label="Comenzi azi"   value={k.orders_today?.value}   delta={k.orders_today?.delta_pct}   icon={ShoppingBag} accent="primary"  spark={sp.orders}     testid="kpi-orders" />
              {canSeeFinance && <KpiTile label="Revenue azi"   value={k.revenue_today?.value}  delta={k.revenue_today?.delta_pct}  icon={TrendingUp}  accent="emerald"  spark={sp.revenue}    format="money" testid="kpi-revenue" />}
              {canSeeFinance && <KpiTile label="Profit azi"    value={k.profit_today?.value}   delta={k.profit_today?.delta_pct}   icon={Wallet}      accent="emerald"  spark={sp.profit}     format="money" testid="kpi-profit" />}
              {canSeeFinance && <KpiTile label="AOV"           value={k.aov_today?.value}      delta={k.aov_today?.delta_pct}      icon={CreditCard}  accent="violet"   spark={sp.aov}        format="money" testid="kpi-aov" />}
              <KpiTile label="Rată livrare"  value={k.delivery_rate?.value}  delta={k.delivery_rate?.delta_pct}  icon={Truck}       accent="emerald"  spark={sp.delivery_rate} format="pct" testid="kpi-delivery-rate" />
              <KpiTile label="Pending"       value={k.pending_orders?.value} icon={ShoppingCart} accent="amber" hint="comenzi de procesat" testid="kpi-pending" />
              <KpiTile label="Retururi active" value={k.returns_active?.value} icon={RotateCcw} accent="rose" testid="kpi-returns-active" />
              <KpiTile label="AWB în tranzit" value={k.awb_in_transit?.value} icon={Truck}     accent="violet" testid="kpi-awb-transit" />
            </>
          )}
        </div>
      </SectionHeader>

      {/* === SECTION 2: LIVE OPERATIONS === */}
      <SectionHeader
        title="Live operations · Ultimele 60 minute"
        subtitle="Evenimente sincronizate prin Shopify + GLS"
        testid="section-live"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <LiveOpCard label="Comenzi noi"        value={liveOps.new_orders_1h}      icon={ShoppingBag}     accent="emerald" testid="live-orders" />
          <LiveOpCard label="AWB-uri generate"   value={liveOps.awb_generated_1h}   icon={Truck}           accent="violet"  testid="live-awb" />
          <LiveOpCard label="Colete ridicate"    value={liveOps.pickups_1h}         icon={Truck}           accent="sky"     testid="live-pickup" />
          <LiveOpCard label="Livrări finalizate" value={liveOps.delivered_1h}       icon={Truck}           accent="emerald" testid="live-delivered" />
          <LiveOpCard label="Retururi noi"       value={liveOps.new_returns_1h}     icon={RotateCcw}       accent="amber"   testid="live-returns" />
          <LiveOpCard label="Probleme critice"   value={liveOps.critical_alerts}    icon={AlertTriangle}   accent="rose"    testid="live-critical" />
          <LiveOpCard label="Operatori activi"   value={liveOps.operators_active}   icon={Users}           accent="sky"     testid="live-operators" />
        </div>
      </SectionHeader>

      {/* === SECTION 7 + 8: ALERTS + ACTIVITY (shown above the fold) === */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2"><AlertsPanel alerts={alerts} loading={loading} /></div>
        <div className="lg:col-span-3"><ActivityFeed /></div>
      </div>

      {/* === SECTION 6: CASHFLOW (admin/manager only) === */}
      {canSeeFinance && (
        <SectionHeader
          title="Revenue & Cashflow"
          subtitle="Estimări agregate din Shopify · COD · Ad spend · Costuri"
          defaultOpen={true}
          testid="section-cashflow"
        >
          {loading ? <Skeleton className="h-44 w-full rounded-xl" /> : <CashflowCard data={data?.cashflow} />}
        </SectionHeader>
      )}

      {/* === SECTION 3: DELIVERY === */}
      {canSeeDelivery && (
        <SectionHeader
          title="Logistică & Livrări"
          subtitle="Performanță curieri, județe, județe problematice"
          defaultOpen={false}
          testid="section-delivery"
        >
          {loading ? <Skeleton className="h-64 w-full rounded-xl" /> : <DeliveryCard data={data?.orders_delivery} />}
        </SectionHeader>
      )}

      {/* === SECTION 4: RETURNS === */}
      {canSeeReturns && (
        <SectionHeader
          title="Retururi · Pipeline"
          subtitle="Distribuție pe etape operaționale"
          defaultOpen={false}
          testid="section-returns"
        >
          {loading ? <Skeleton className="h-64 w-full rounded-xl" /> : <ReturnsCard data={data?.returns} />}
        </SectionHeader>
      )}

      {/* === FOOTER QUICK NAV === */}
      <div className="border-t border-border pt-6 flex items-center justify-between flex-wrap gap-3">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
          Generat: <span className="font-mono text-foreground/80">{data?.generated_at ? new Date(data.generated_at).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—"}</span>
          {" · "}auto-refresh la {POLL_MS / 1000}s · realtime SSE
        </div>
        <div className="flex items-center gap-3 text-xs">
          <QuickLink to="/comenzi" icon={ShoppingBag} label="Comenzi" />
          <QuickLink to="/retururi" icon={RotateCcw} label="Retururi" />
          <QuickLink to="/livrari" icon={Truck} label="Livrări" />
          <QuickLink to="/stocuri" icon={Boxes} label="Stocuri" />
          <QuickLink to="/analytics" icon={Activity} label="Analytics" />
        </div>
      </div>
    </div>
  );
}

function RealtimePill({ status }) {
  const v = status === "live"
    ? { dot: "bg-emerald-500", ring: "bg-emerald-500/40", text: "Live" }
    : status === "reconnecting" || status === "connecting"
    ? { dot: "bg-amber-500",   ring: "bg-amber-500/40",   text: "Conectare…" }
    : { dot: "bg-zinc-400",    ring: "bg-zinc-400/30",    text: "Offline" };
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-2.5 h-8 text-[11px] font-medium" data-testid="dashboard-live-pill">
      <span className="relative inline-flex h-2 w-2">
        {status === "live" && <span className={`absolute inset-0 inline-flex h-full w-full rounded-full opacity-75 animate-ping ${v.ring}`} />}
        <span className={`relative inline-flex h-2 w-2 rounded-full ${v.dot}`} />
      </span>
      {v.text}
    </div>
  );
}

function QuickLink({ to, icon: Icon, label }) {
  return (
    <Link to={to} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-secondary transition">
      <Icon className="h-3.5 w-3.5 text-muted-foreground" /> {label}
      <ExternalLink className="h-3 w-3 text-muted-foreground/60" />
    </Link>
  );
}

function KpiSkeleton() {
  return (
    <div className="rounded-xl border border-border bg-white p-4">
      <Skeleton className="h-3 w-20 mb-3" />
      <Skeleton className="h-8 w-24 mb-2" />
      <Skeleton className="h-2 w-12" />
    </div>
  );
}
